//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { BodyText, SectionText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import RequestModalSuccessIcon from '@images/campaigns/sampling/request-modal-success.svg';
import { Mixins } from '@theme/mixins';

interface PaymentSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PaymentSuccessModal = ({
  isOpen,
  onClose,
}: PaymentSuccessModalProps) => {
  return (
    <Modal
      closeModal={onClose}
      contents={
        <Container>
          <img alt='Success' src={RequestModalSuccessIcon} />
          <ContainerHeader>
            <SectionText>Campaign Payment Processed</SectionText>
          </ContainerHeader>
          <MessageContainer>
            <BodyText>
              Your campaign request has been submitted, and our team is
              currently reviewing it.
            </BodyText>
            <BodyText>
              If there are no issues, the request will be approved, and we'll
              begin preparing your campaign for launch. Otherwise, we'll be in
              touch to resolve any outstanding details.
            </BodyText>
            <BodyText>
              <BoldSpan>Note:</BoldSpan> Payment for your request will remain on
              hold until the campaign is approved, after which it will be
              processed.
            </BodyText>
          </MessageContainer>
        </Container>
      }
      isFooterEnabled
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent: 'Close',
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.REQUEST_CAMPAIGN_PAYMENT_MODAL_SUCCESS_BUTTON,
        onClick: onClose,
      }}
      shouldIncludeSecondaryButton={false}
      shouldIncludeTertiaryButton={false}
      title=''
      width='wide'
    />
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.echo.spacing.sm};
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const ContainerHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.echo.spacing.sm};
  align-items: center;
  justify-content: center;
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.echo.spacing.sm};
  width: 100%;
  max-width: 560px;
  text-align: center;
`;

const BoldSpan = styled.span`
  ${Mixins.Text.BodyBold};
`;
